/**
 * Your own history on the Omni-Torn server (terms 19 — packages/shared own-history.ts; owner's call 2026-11-02 18:44:
 * "Everything anyone requests should be cached and stored on server"). Pages ask the server first, read from Torn only
 * what it doesn't have, and send each Torn read back (one upload per read) so it's never read twice — on any device.
 * Below terms 19 (`ownHistoryOn()` false) nothing is sent and pages keep their older way.
 */
import type { OwnStream, Span } from '@omni-torn/shared';
import { server, session } from './session';

/** Is your own history kept on the server (terms 19)? */
export const ownHistoryOn = (): boolean => session.me()?.ownHistory === true;

export interface OwnRead {
  on: boolean;
  entries: Record<string, unknown>;
  seq: number;
  last: number;
  more: boolean;
  count: number;
  spans: Span[];
}

/** What the server kept: `since` (its order of keeping: catching a copy up) or newest first `before` a time (ms). */
export function ownRead(stream: OwnStream, q: { since?: number; before?: number; limit?: number; cat?: number | null } = {}): Promise<OwnRead> {
  const p = new URLSearchParams();
  if (q.since !== undefined) p.set('since', String(q.since));
  if (q.before !== undefined) p.set('before', String(q.before));
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.cat) p.set('cat', String(q.cat));
  return server<OwnRead>('GET', `/api/me/own/${stream}?${p}`);
}

/**
 * One Torn read of yours, sent to the server (Torn's entries as they came, and the stretch the read covered in full). Never
 * throws: a read that couldn't be sent is only read again later.
 */
export async function ownSend(stream: OwnStream, entries: Record<string, unknown> | unknown[], span: Span | null, cat?: number | null): Promise<Span[] | null> {
  if (!ownHistoryOn()) return null;
  const r = await server<{ kept?: boolean; spans?: Span[] }>('POST', `/api/me/own/${stream}`, { entries, span, ...(cat ? { cat } : {}) }).catch(() => null);
  return r?.kept ? (r.spans ?? []) : null;
}

// ---------------------------------------------------------------- the words pages use about where your log is kept

/** "kept for you on Omni-Torn" (terms 19) or "kept in this browser" (below 19). */
export const logKeptWhere = (): string => (ownHistoryOn() ? 'kept for you on Omni-Torn' : 'kept in this browser');

/** The line shown while a log page opens. */
export const readingLogText = (): string => `Reading the log ${logKeptWhere()}…`;

/** The question before "Forget": from 19 only this browser's copy goes (the server keeps yours until you delete your data). */
export const forgetLogText = (extra = ''): string =>
  ownHistoryOn()
    ? `Delete this browser’s copy of your log${extra}? Omni-Torn still keeps your log for you (until you delete your data); this page fetches it again next time.`
    : `Delete the log${extra} kept in this browser? Torn still has ${extra ? 'them' : 'it'}; you can read ${extra ? 'them' : 'it'} again.`;
